
import type { ExistingAttachment } from '../../../components/AttachmentManager'
import { paymentMethodLabel } from '../../../utils/paymentMethodLabels'

export const compareSizes = (a: string, b: string): number => {
  const isNumA = /^\d+$/.test(a);
  const isNumB = /^\d+$/.test(b);

  if (isNumA && isNumB) {
    return Number(a) - Number(b);
  }
  if (isNumA && !isNumB) {
    return -1;
  }
  if (!isNumA && isNumB) {
    return 1;
  }

  const textOrder = [
    'PP', 'P', 'M', 'G', 'GG', 'XL', 'XLL',
    'G1', 'G2', 'G3', 'G4',
    'TAMANHO ÚNICO', 'ÚNICO', 'U'
  ];

  const normA = a.trim().toUpperCase();
  const normB = b.trim().toUpperCase();

  const idxA = textOrder.indexOf(normA);
  const idxB = textOrder.indexOf(normB);

  if (idxA !== -1 && idxB !== -1) {
    return idxA - idxB;
  }
  if (idxA !== -1 && idxB === -1) {
    return -1;
  }
  if (idxA === -1 && idxB !== -1) {
    return 1;
  }

  return a.localeCompare(b);
};


export interface FashionOrderProductItemInput {
  colorId: number
  size: string
  quantity: number
}

export type SizeQuantities = { [size: string]: number }

export interface ProductApiData {
  fashionProductId?: number
  discount: number | null
  sellingPrice?: number | null
  totalItems: number
  subtotal: number
  items: FashionOrderProductItemInput[]
  fashionProduct?: {
    id: number
    name: string
    price: number
    group: { id: number; name?: string; family?: { id: number; name?: string } }
  }
  colorCount?: number
  family?: { id: number; name?: string }
  group?: { id: number; name?: string }
  collectionName?: string
  sizeQuantities?: SizeQuantities
  kitIndex?: number | null
  colors?: string[]
  selectedColors?: number[]
  nomeCustomizado?: string
}

export interface CompanyItemType {
  id: number
  companyName: string
}

export interface UserItemType {
  id: number
  name: string
}

export type FormViewState = {
  supplier: CompanyItemType & {
    cnpj?: string
    telefone?: string
    email?: string
    inscricaoEstadual?: string
  }
  seller: UserItemType
  company: CompanyItemType & {
    cnpj?: string
    inscricaoEstadual?: string
    endereco?: string
    bairro?: string
    cep?: string
    cidade?: string
    estado?: string
    telefone?: string
    email?: string
    nomeFantasia?: string
  }
  orderType: string
  paymentCondition: string
  shippingType: string
  trackingCode: string
  finalDateOfDelivery: string
  startDateOfDelivery: string
  extraInformation: string
  quantityItems: string
  quantityPieces: string
  totalCost: string
  sellingPriceLabel: string
  shippingCost: string
  maximumBillingAmount: string
  carrier?: CompanyItemType | null
  clientOrderNumber?: string
  paymentMethod?: string
  billingDate?: string
  installments?: Array<{
    id?: number
    numero: number
    valor: number
    vencimento: string
  }>
  representative?: string
  orderSituation?: string
  generalClass?: string
  orderDate?: string
  proposalNumber?: number | null
  saleNumber?: number | null
  saleStatus?: string | null
}

export const formatDate = (iso: string | null | undefined): string =>
  iso ? iso.substring(0, 10) : ''

export const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)

export const formatMaximumBillingDisplay = (value: unknown): string => {
  if (value === null || value === undefined || value === '') return ''
  const digits = String(value).replace(/\D/g, '')
  if (!digits) return ''
  return formatCurrency(Number(digits) / 100)
}

export const countColors = (product: ProductApiData): number => {
  if (product.colorCount && product.colorCount > 0) return product.colorCount
  if (product.items?.length) return new Set(product.items.map((i) => i.colorId)).size
  return 0
}

export function normalizeProductData(product: Record<string, unknown>): ProductApiData {
  const sizeQuantities: SizeQuantities = {}
  const items = (product.items || []) as FashionOrderProductItemInput[]
  items.sort((a, b) => compareSizes(a.size, b.size))
  items.forEach((item) => {
    if (item.size && item.quantity !== undefined)
      sizeQuantities[item.size] = (sizeQuantities[item.size] || 0) + Number(item.quantity)
  })
  const calculatedItems = Object.values(sizeQuantities).reduce((sum, qty) => sum + qty, 0)
  const totalItems = calculatedItems > 0 ? calculatedItems : (Number(product.totalItems) || 0)
  const fp = product.fashionProduct as ProductApiData['fashionProduct']
  const unitPrice = Number(fp?.price || (product as any).price || 0)
  const rawSubtotal = product.subtotal != null ? Number(product.subtotal) : 0
  const subtotal = rawSubtotal > 0 ? rawSubtotal : totalItems * unitPrice
  const colorCount =
    (product.colorCount as number | undefined) || new Set(items.map((i) => i.colorId)).size

  return {
    ...(product as unknown as ProductApiData),
    sizeQuantities,
    totalItems,
    subtotal,
    colorCount,
    items,
    kitIndex: product.kitIndex !== undefined ? (product.kitIndex as number | null) : null,
  }
}

export function effectiveItemPrice(
  product: { subtotal?: number | null; sellingPrice?: number | null },
): number {
  /* Ausente é `null`, não zero: desconto de 100% é venda por R$ 0,00. */
  return product.sellingPrice != null
    ? Number(product.sellingPrice)
    : Number(product.subtotal ?? 0)
}

export function getTotalPieces(product: ProductApiData): number {
  return product.sizeQuantities
    ? Object.values(product.sizeQuantities).reduce((s, q) => s + q, 0)
    : product.totalItems ?? 0
}

/* Vem dos dois preços: o campo `discount` não guarda o ajuste de quem vendeu acima do cadastrado. */
export function formatProductDiscountLabel(product: ProductApiData): string {
  const cadastrado = Number(product.subtotal ?? 0)
  if (cadastrado <= 0) return '-'
  const diferenca = cadastrado - effectiveItemPrice(product)
  if (Math.abs(diferenca) < 0.01) return '-'
  const pct = (diferenca / cadastrado) * 100
  const percentual = `${pct < 0 ? '+' : ''}${Math.abs(pct).toFixed(1).replace('.', ',')}%`
  return `${percentual} (${diferenca < 0 ? '+' : '−'}${formatCurrency(Math.abs(diferenca))})`
}

export interface FashionOrderDetailMapped {
  formState: FormViewState
  addedProducts: ProductApiData[]
  kitCount: number
  attachments: ExistingAttachment[]
}

export function mapSteelOrderResponseToDetail(data: Record<string, unknown>): FashionOrderDetailMapped {
  const getLabelMain = (item: any): string => {
    if (!item || !item.isMain) return '';
    const tipo = item.tipoEstabelecimento || (item.cnpj ? (item.cnpj.replace(/\D/g, '').substring(8, 12) === '0001' ? 'Matriz' : 'Filial') : '');
    return tipo ? ` - ${tipo}` : '';
  };

  const supDetails = (data.supplierDetails as Record<string, any>) || {}
  const cliDetails = (data.clientDetails as Record<string, any>) || {}

  const formState: FormViewState = {
    supplier: {
      id: Number(data.supplierId),
      companyName: `${(data.supplierDetails as { name?: string } | undefined)?.name || String(data.supplier || '')}${getLabelMain(data.supplierDetails)}`,
      cnpj: supDetails.cnpj || supDetails.document || '',
      telefone: supDetails.telefone || supDetails.phone || supDetails.telephone || '',
      email: supDetails.email || '',
      inscricaoEstadual: supDetails.inscricaoEstadual || supDetails.stateRegistration || supDetails.stateInscription || supDetails.ie || '',
    },
    seller: {
      id: (data.sellerId as number) || 0,
      name: (data.sellerDetails as { name?: string } | undefined)?.name || String(data.seller || ''),
    },
    company: {
      id: Number(data.clientId),
      companyName: `${(data.clientDetails as { name?: string } | undefined)?.name || String(data.client || '')}${getLabelMain(data.clientDetails)}`,
      cnpj: cliDetails.cnpj || cliDetails.document || '',
      inscricaoEstadual: cliDetails.inscricaoEstadual || cliDetails.stateInscription || cliDetails.ie || '',
      endereco: cliDetails.endereco || cliDetails.address || cliDetails.logradouro || '',
      bairro: cliDetails.bairro || cliDetails.neighborhood || '',
      cep: cliDetails.cep || cliDetails.zipCode || '',
      cidade: cliDetails.cidade || cliDetails.city || '',
      estado: cliDetails.estado || cliDetails.state || cliDetails.uf || '',
      telefone: cliDetails.telefone || cliDetails.phone || cliDetails.telephone || '',
      email: cliDetails.email || '',
      nomeFantasia: cliDetails.nomeFantasia || cliDetails.fantasyName || cliDetails.name || '',
    },
    orderType:
      typeof data.orderType === 'object' && data.orderType !== null
        ? String((data.orderType as { name?: string }).name || '')
        : String(data.orderType || ''),
    paymentCondition: String(
      (data.paymentTerm as string | undefined) ||
        (typeof data.paymentCondition === 'object' && data.paymentCondition !== null
          ? (data.paymentCondition as { name?: string }).name
          : (data.paymentCondition as string | undefined)) ||
        '',
    ),
    shippingType:
      typeof data.shippingType === 'object' && data.shippingType !== null
        ? String((data.shippingType as { name?: string }).name || '')
        : String(data.shippingType || ''),
    trackingCode: String(data.trackingCode || ''),
    finalDateOfDelivery: formatDate(data.finalDateOfDelivery as string | undefined),
    startDateOfDelivery: formatDate(
      (data.startDateOfDelivery as string | undefined) || (data.orderDate as string | undefined),
    ),
    extraInformation: String(data.extraInfo || data.extraInformation || ''),
    quantityItems: String(data.quantityItems || '0'),
    quantityPieces: String(data.quantityPieces || '0'),
    totalCost: formatCurrency(Number(data.totalCost || 0)),
    sellingPriceLabel: data.sellingPrice != null
      ? formatCurrency(Number(data.sellingPrice))
      : data.total != null && Number(data.total) > 0
        ? formatCurrency(Number(data.total))
        : '',
    shippingCost:
      data.shippingValue != null
        ? formatCurrency(Number(data.shippingValue))
        : data.shippingCost != null
          ? formatCurrency(Number(data.shippingCost))
          : '',
    maximumBillingAmount: formatMaximumBillingDisplay(data.maximumBillingAmount),
    carrier: data.carrier ? { id: Number((data.carrier as any).id), companyName: (data.carrier as any).companyName || '' } : null,
    clientOrderNumber: data.clientOrderNumber ? String(data.clientOrderNumber) : '',
    representative: data.representative
      ? typeof data.representative === 'object' && data.representative !== null
        ? String((data.representative as { name?: string }).name || '')
        : String(data.representative)
      : '',
    orderSituation: data.orderSituation
      ? typeof data.orderSituation === 'object' && data.orderSituation !== null
        ? String((data.orderSituation as { name?: string }).name || '')
        : String(data.orderSituation)
      : '',
    generalClass: data.generalClass
      ? typeof data.generalClass === 'object' && data.generalClass !== null
        ? String((data.generalClass as { name?: string }).name || '')
        : String(data.generalClass)
      : '',
    orderDate: data.orderDate ? String(data.orderDate).substring(0, 10) : '',
    proposalNumber: data.proposalNumber != null ? Number(data.proposalNumber) : null,
    saleNumber: data.saleNumber != null ? Number(data.saleNumber) : null,
    saleStatus: data.saleStatus != null ? String(data.saleStatus) : null,
    paymentMethod: (() => {
      const pmRelation = ((data.OrderPaymentMethod || data.orderPaymentMethod || data.paymentMethods || data.orderPaymentMethods || []) as any)?.[0]
      const pmName = pmRelation?.PaymentMethod?.name || pmRelation?.paymentMethod?.name || ''
      return pmName ? paymentMethodLabel(pmName) : ''
    })(),
    billingDate: data.billingDate ? String(data.billingDate).substring(0, 10) : '',
    installments: (((data.OrderInstallment || data.orderInstallment || []) as Record<string, any>[]).map((inst) => ({
      id: inst.id ? Number(inst.id) : undefined,
      numero: Number(inst.numero || 0),
      valor: Number(inst.valor || 0),
      vencimento: inst.vencimento ? String(inst.vencimento).substring(0, 10) : '',
    }))).sort((a, b) => a.numero - b.numero),
  }

  const attachments: ExistingAttachment[] = ((data.attachments || []) as Record<string, unknown>[]).map((a) => ({
    id: Number(a.id),
    originalName: String(a.originalName || a.title || a.fileName || 'Arquivo'),
    fileName: String(a.fileName || a.title || ''),
    filePath: String(a.fileUrl || a.filePath || ''),
    fileSize: Number(a.fileSize) || 0,
    mimeType: String(a.mimeType || ''),
  }))

  const products = ((data.items || []) as Record<string, unknown>[])
    .filter((item) => item.itemType === 'PRODUCT')
    .map((item) => {
      let extra: Record<string, unknown> = {}
      try {
        extra = item.extraInfo ? JSON.parse(String(item.extraInfo)) : {}
      } catch {
        extra = {}
      }
      const P = item.Product as
        | {
            Family?: { id?: number; name?: string; Collection?: { name?: string } }
            family?: { id?: number; name?: string }
            group?: { id?: number; name?: string }
            Collection?: { name?: string }
            name?: string
            price?: number
          }
        | undefined
      const famRel = P?.Family || P?.family
      const famId = Number(extra.familyId ?? famRel?.id ?? 0)
      const famName = String(extra.familyName || famRel?.name || '').trim()
      const collName = String(
        extra.collectionName || 
        (P as any)?.Collection?.name || 
        (P as any)?.Family?.Collection?.name || 
        ''
      ).trim()

      const unitPrice = Number(extra.fashionProductPrice || P?.price || 0)
      const totalPieces = Number(item.quantity) || 0
      const rawPrice = Number(item.itemPrice ?? item.priceKg) || 0
      const subtotal = rawPrice > 0 ? rawPrice : totalPieces * unitPrice

      return normalizeProductData({
        fashionProductId: Number(item.productId),
        fashionProduct: {
          id: Number(item.productId || 0),
          name: String(extra.fashionProductName || P?.name || ''),
          price: unitPrice,
          group: {
            id: Number(extra.groupId || P?.group?.id),
            name: String(extra.groupName || P?.group?.name || ''),
            family: {
              id: famId,
              name: famName,
            },
          },
        },
        family: famName ? { id: famId, name: famName } : undefined,
        collectionName: collName,
        discount: (item.descount as number) || 0,
        totalItems: totalPieces,
        subtotal,
        sellingPrice: item.sellingPrice != null ? Number(item.sellingPrice) : null,
        items: (extra.fashionItems as FashionOrderProductItemInput[]) || [],
        colorCount: Number(extra.colorCount) || 0,
        kitIndex: item.kitIndex !== undefined ? (item.kitIndex as number | null) : null,
        colors: (extra.colors as string[]) || [],
        selectedColors: (extra.selectedColors as number[]) || [],
        nomeCustomizado: typeof item.nomeCustomizado === 'string' ? item.nomeCustomizado : undefined,
      })
    })

  const maxKit = products.reduce((max: number, p: ProductApiData) => {
    if (p.kitIndex !== null && p.kitIndex !== undefined && p.kitIndex > max) return p.kitIndex
    return max
  }, -1)
  const kitCount = maxKit >= 0 ? maxKit + 1 : 0

  return { formState, addedProducts: products, kitCount, attachments }
}
